import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactSection = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', organization: '', message: '' });
    } catch (err) {
      setError(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setIsSubmitted(false);
        setError('');
      }, 5000);
    }
  };

  return (
    <section id="contact" className="section-padding section-grey border-t border-gray-200 relative">
      {/* Strategic lines */}
      <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
      
      <div className="container mx-auto px-8 max-w-6xl">
        <div className="grid md:grid-cols-12 gap-16">
          {/* Left: Title */}
          <div className="md:col-span-5">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-8 font-light">{t.contact.label}</p>
            <h2 className="text-6xl md:text-7xl font-normal text-gray-900 mb-10 leading-tight tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.contact.title1}<br />{t.contact.title2}<br />{t.contact.title3}
            </h2>
            <div className="w-20 h-px bg-gray-300 mb-10" />
            <p className="text-lg text-gray-600 font-light leading-relaxed mb-8">
              {t.contact.description}
            </p>
            <p className="text-sm text-gray-500 font-light">
              {t.contact.confidentiality}
            </p>
          </div>

          {/* Right: Form */}
          <div className="md:col-span-7 bg-white p-10 subtle-shadow border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-gray-500 text-xs tracking-widest uppercase mb-4 font-light">
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-gray-300 focus:border-gray-900 text-gray-900 py-3 outline-none transition-colors font-light"
                  />
                </div>
                <div>
                  <label className="block text-gray-500 text-xs tracking-widest uppercase mb-4 font-light">
                    {t.contact.form.email}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-gray-300 focus:border-gray-900 text-gray-900 py-3 outline-none transition-colors font-light"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-4 font-light">
                  {t.contact.form.organization}
                </label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-gray-300 focus:border-gray-900 text-gray-900 py-3 outline-none transition-colors font-light"
                />
              </div>

              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-4 font-light">
                  {t.contact.form.message}
                </label>
                <textarea
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-transparent border border-gray-300 focus:border-gray-900 text-gray-900 p-4 outline-none transition-colors resize-none font-light"
                />
              </div>

              <div className="text-xs text-gray-500 font-light pt-2">
                {t.contact.form.encryption}
              </div>

              {error && (
                <div className="text-sm text-red-600 font-light">{error}</div>
              )}

              {isSubmitted && (
                <div className="text-sm text-gray-700 font-light">
                  {t.contact.form.success}
                </div>
              )}

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || isSubmitted}
                  className="border border-gray-400 hover:border-gray-900 text-gray-900 px-12 py-4 text-sm tracking-[0.2em] uppercase transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-3 subtle-shadow hover:shadow-md"
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSubmitted ? t.contact.form.submitted : t.contact.form.submit}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
